import api from './axios';

/**
 * Search for users, posts, and communities
 * @param {Object} options - Search options
 * @param {string} options.q - Search query
 * @param {string} options.type - Type of results to return (users, posts, communities)
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {Object} options - Additional filters
 * @returns {Promise} Promise object representing the API response
 */
export const search = (options = {}) => {
  const { 
    q, 
    type, 
    page = 1, 
    limit = 10,
    sortBy,
    userLocation,
    userSkills,
    postDate,
    hasTags,
    communityCategory,
    createdAfter,
    createdBefore,
    ...otherFilters
  } = options;
  
  return api.get('/search', {
    params: {
      q,
      type,
      page,
      limit,
      sortBy,
      userLocation,
      userSkills,
      postDate,
      hasTags,
      communityCategory,
      createdAfter,
      createdBefore,
      ...otherFilters
    },
  });
};

/**
 * Search for users
 * @param {string} query - Search query
 * @param {Object} options - Search options and pagination
 * @returns {Promise} Promise object representing the API response
 */
export const searchUsers = (query, options = {}) => {
  const { 
    page = 1, 
    limit = 10,
    sortBy,
    userLocation,
    userSkills,
    createdAfter,
    createdBefore,
    ...otherFilters
  } = options;
  
  return api.get('/search/users', {
    params: {
      q: query,
      page,
      limit,
      sortBy,
      location: userLocation,
      skills: userSkills,
      createdAfter,
      createdBefore,
      ...otherFilters
    },
  });
};

/**
 * Search for posts
 * @param {string} query - Search query
 * @param {Object} options - Search options and pagination
 * @returns {Promise} Promise object representing the API response
 */
export const searchPosts = (query, options = {}) => {
  const { 
    page = 1, 
    limit = 10,
    sortBy,
    postDate,
    hasTags,
    createdAfter,
    createdBefore,
    ...otherFilters
  } = options;
  
  return api.get('/search/posts', {
    params: {
      q: query,
      page,
      limit,
      sortBy,
      dateRange: postDate,
      tags: hasTags,
      createdAfter,
      createdBefore,
      ...otherFilters
    },
  });
};

/**
 * Search for communities
 * @param {string} query - Search query
 * @param {Object} options - Search options and pagination
 * @returns {Promise} Promise object representing the API response
 */
export const searchCommunities = (query, options = {}) => {
  const { 
    page = 1, 
    limit = 10,
    sortBy,
    communityCategory,
    createdAfter,
    createdBefore,
    ...otherFilters
  } = options;
  
  return api.get('/search/communities', {
    params: {
      q: query,
      page,
      limit,
      sortBy,
      category: communityCategory,
      createdAfter,
      createdBefore,
      ...otherFilters
    },
  });
};

/**
 * Get trending searches, users, posts, and communities
 * @returns {Promise} Promise object representing the API response
 */
export const getTrendingSearches = () => {
  return api.get('/search/trending');
};

/**
 * Get user suggestions based on search term
 * @param {string} query - Search query
 * @param {number} limit - Number of suggestions to return
 * @returns {Promise} Promise object representing the API response
 */
export const getUserSuggestions = (query, limit = 5) => {
  return api.get('/search/users', {
    params: {
      q: query,
      limit,
      suggestions: true
    }
  });
};